/**
 * System Workflow Templates
 *
 * MOVED TO: /types/WorkflowTypes.ts
 *
 * This file contained hardcoded templates which violated FERP data-driven architecture.
 * All workflow templates now come from backend APIs only.
 *
 * Use: WorkflowTemplateService to fetch templates from backend
 */

import * as z from 'zod';
import { InstanceRepository } from '../data/repositories/InstanceRepository';
import WorkflowDraftRepository, { DraftRecord } from '../data/repositories/WorkflowDraftRepository';
export * from '../types/WorkflowTypes';
export interface WorkflowTemplate {
  id: string;
  sk: string;
  template_name: string;
  version: string;
  display_name: string;
  description: string;
  category: WorkflowCategory;
  scope: WorkflowScope;
  definition: string;
  parameters: TemplateParameter[];
  ui_config: WorkflowUIConfig;
  compliance_frameworks: string[];
  required_roles: string[];
  is_system_default: boolean;
  created_at: number;
  updated_at: number;
  created_by: string;
}

export enum WorkflowCategory {
  UserManagement = 'UserManagement',
  DocumentApproval = 'DocumentApproval',
  ComplianceReview = 'ComplianceReview',
  SystemIntegration = 'SystemIntegration',
  DataProcessing = 'DataProcessing',
  SecurityIncident = 'SecurityIncident',
  TenantOnboarding = 'TenantOnboarding',
  PolicyManagement = 'PolicyManagement',
  AuditProcess = 'AuditProcess',
  BusinessProcess = 'BusinessProcess',
}

export enum WorkflowScope {
  System = 'System',
  Tenant = 'Tenant',
  Domain = 'Domain',
}

export interface TemplateParameter {
  name: string;
  parameter_type: ParameterType;
  display_name: string;
  description: string;
  required: boolean;
  default_value?: string;
  validation_rules: ValidationRule[];
  ui_component: UIComponent;
}

export enum ParameterType {
  String = 'String',
  Number = 'Number',
  Boolean = 'Boolean',
  Array = 'Array',
  Object = 'Object',
  Email = 'Email',
  PhoneNumber = 'PhoneNumber',
  Date = 'Date',
  DateTime = 'DateTime',
  TenantId = 'TenantId',
  UserId = 'UserId',
  RoleList = 'RoleList',
}

export interface ValidationRule {
  rule_type: string;
  value: string;
  error_message: string;
}

export interface UIComponent {
  component_type: string;
  props: Record<string, unknown>;
}

export interface WorkflowUIConfig {
  form_layout: FormLayout;
  step_indicators: StepIndicator[];
  field_groups: FieldGroup[];
  validation_messages: Record<string, string>;
  submit_button_text: string;
  cancel_button_text: string;
}

export interface FormLayout {
  layout_type: string;
  columns: number;
  spacing: string;
}

export interface StepIndicator {
  step_number: number;
  title: string;
  description: string;
  state_name: string;
}

export interface FieldGroup {
  title: string;
  description?: string;
  fields: string[];
  collapsible: boolean;
  expanded_by_default: boolean;
}

// Frontend form configuration generated from templates
export interface WorkflowFormConfig {
  workflow_name: string;
  display_name: string;
  description: string;
  form_fields: FormField[];
  ui_layout: UILayout;
  validation_rules: Record<string, ValidationRule[]>;
  validation_messages: Record<string, string>;
  submission_config: SubmissionConfig;
}

export interface FormField {
  name: string;
  field_type: string;
  display_name: string;
  description: string;
  required: boolean;
  default_value?: string;
  component_type: string;
  component_props: Record<string, unknown>;
  validation_rules: string[];
  depends_on?: string;
  conditional_display?: ConditionalDisplay;
}

export interface ConditionalDisplay {
  field: string;
  condition: string;
  value: unknown;
}

export interface UILayout {
  layout_type: string;
  columns: number;
  spacing: string;
  step_indicators: StepIndicator[];
  field_groups: FieldGroup[];
  progressive_disclosure: boolean;
}

export interface SubmissionConfig {
  submit_button_text: string;
  cancel_button_text: string;
  confirmation_required: boolean;
  auto_save: boolean;
  save_draft: boolean;
}

// Validation schemas for form data
export const createWorkflowFormSchema = (config: WorkflowFormConfig) => {
  const schema: Record<string, z.ZodType> = {};

  config.form_fields.forEach((field) => {
    let fieldSchema: z.ZodType;

    switch (field.field_type) {
      case 'string':
        fieldSchema = z.string();
        break;
      case 'number':
        fieldSchema = z.number();
        break;
      case 'boolean':
        fieldSchema = z.boolean();
        break;
      case 'email':
        fieldSchema = z.string().email(config.validation_messages.invalid_email || 'Invalid email');
        break;
      case 'phone':
        fieldSchema = z
          .string()
          .regex(
            /^\+?[1-9]\d{1,14}$/,
            config.validation_messages.invalid_phone || 'Invalid phone number',
          );
        break;
      case 'date':
        fieldSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Invalid date format');
        break;
      case 'array':
        fieldSchema = z.array(z.string());
        break;
      default:
        fieldSchema = z.string();
    }

    // Apply validation rules
    field.validation_rules.forEach((rule) => {
      const validationRule = config.validation_rules[rule];
      if (validationRule) {
        validationRule.forEach((r) => {
          switch (r.rule_type) {
            case 'minLength':
              if (fieldSchema instanceof z.ZodString) {
                fieldSchema = fieldSchema.min(parseInt(r.value), r.error_message);
              }
              break;
            case 'maxLength':
              if (fieldSchema instanceof z.ZodString) {
                fieldSchema = fieldSchema.max(parseInt(r.value), r.error_message);
              }
              break;
            case 'pattern':
              if (fieldSchema instanceof z.ZodString) {
                fieldSchema = fieldSchema.regex(new RegExp(r.value), r.error_message);
              }
              break;
            case 'min':
              if (fieldSchema instanceof z.ZodNumber) {
                fieldSchema = fieldSchema.min(parseFloat(r.value), r.error_message);
              }
              break;
            case 'max':
              if (fieldSchema instanceof z.ZodNumber) {
                fieldSchema = fieldSchema.max(parseFloat(r.value), r.error_message);
              }
              break;
          }
        });
      }
    });

    // Make optional if not required
    if (!field.required) {
      fieldSchema = fieldSchema.optional();
    }

    schema[field.name] = fieldSchema;
  });

  return z.object(schema);
};

// API service for workflow templates using FERP repository pattern

export class WorkflowTemplateService {
  private draftRepository: WorkflowDraftRepository;
  private instanceRepository: InstanceRepository;

  constructor(deps?: {
    draftRepository?: WorkflowDraftRepository;
    instanceRepository?: InstanceRepository;
  }) {
    this.draftRepository = deps?.draftRepository ?? new WorkflowDraftRepository();
    this.instanceRepository = deps?.instanceRepository ?? new InstanceRepository();
  }

  /**
   * Get all available workflow definitions (templates)
   */
  async getAvailableWorkflows(tenantId: string): Promise<WorkflowTemplate[]> {
    const drafts = await this.draftRepository.listWorkflowDefinitions(tenantId);
    return drafts
      .filter((draft) => !draft.deleted)
      .map((draft) => this.transformDraftToWorkflowTemplate(draft));
  }

  /**
   * Get workflows by category
   */
  async getWorkflowsByCategory(
    tenantId: string,
    category: WorkflowCategory,
  ): Promise<WorkflowTemplate[]> {
    const allWorkflows = await this.getAvailableWorkflows(tenantId);
    return allWorkflows.filter((workflow) => workflow.category === category);
  }

  /**
   * Generate form configuration for a workflow
   */
  async generateFormConfig(workflowName: string, tenantId: string): Promise<WorkflowFormConfig> {
    // Get the workflow definition from drafts
    const workflows = await this.getAvailableWorkflows(tenantId);
    const workflow = workflows.find((w) => w.template_name === workflowName);

    if (!workflow) {
      throw new Error(`Workflow template ${workflowName} not found`);
    }

    // Transform workflow definition to form config
    return this.transformWorkflowToFormConfig(workflow);
  }

  /**
   * Start a workflow instance using FERP primitives
   */
  async startWorkflow(
    workflowName: string,
    tenantId: string,
    userId: string,
    sessionId: string,
    inputData: Record<string, unknown>,
  ): Promise<{ instance_id: string }> {
    const instanceId = `${workflowName}-${tenantId}`;
    await this.draftRepository.createWorkflowInstanceDraft({
      tenant: tenantId,
      key: instanceId,
      owner: userId,
      data: {
        workflow_name: workflowName,
        status: 'Active',
        context: inputData,
        created_at: Date.now(),
        created_by: userId,
        session_id: sessionId,
      },
    });

    return { instance_id: instanceId };
  }

  /**
   * Get workflow instance details
   */
  async getWorkflowInstance(instanceId: string, tenantId: string): Promise<WorkflowInstance> {
    const drafts = await this.draftRepository.listWorkflowInstances(tenantId);
    const instanceDraft = drafts.find((draft) => draft.key === instanceId);
    if (!instanceDraft) {
      throw new Error(`Workflow instance ${instanceId} not found`);
    }

    return this.transformDraftToWorkflowInstance(instanceDraft);
  }

  /**
   * Get tenant workflow instances
   */
  async getTenantWorkflowInstances(
    tenantId: string,
    workflowName?: string,
  ): Promise<WorkflowInstance[]> {
    const drafts = await this.draftRepository.listWorkflowInstances(tenantId);

    let instances = drafts
      .filter((draft) => !draft.deleted)
      .map((draft) => this.transformDraftToWorkflowInstance(draft));

    if (workflowName) {
      instances = instances.filter(
        (instance: WorkflowInstance) => instance.workflow_name === workflowName,
      );
    }

    return instances;
  }

  private transformDraftToWorkflowTemplate(draft: DraftRecord): WorkflowTemplate {
    // Transform FERP draft format to WorkflowTemplate interface
    const data = draft.data || {};
    return {
      id: draft.key,
      sk: `WORKFLOW#${draft.key}`,
      template_name: data.name || draft.key,
      version: data.version || '1.0.0',
      display_name: data.display_name || data.name || draft.key,
      description: data.description || '',
      category: data.category || WorkflowCategory.BusinessProcess,
      scope: data.scope || WorkflowScope.Tenant,
      definition: data.definition || '',
      parameters: data.parameters || [],
      ui_config: data.ui_config || this.getDefaultUIConfig(),
      compliance_frameworks: data.compliance_frameworks || [],
      required_roles: data.required_roles || [],
      is_system_default: data.is_system_default || false,
      created_at: draft.updatedMs,
      updated_at: draft.updatedMs,
      created_by: draft.owner || 'system',
    };
  }

  private transformDraftToWorkflowInstance(draft: DraftRecord): WorkflowInstance {
    const data = draft.data || {};
    return {
      id: draft.key,
      workflow_name: data.workflow_name || '',
      workflow_version: data.workflow_version || '1.0.0',
      tenant_id: draft.tenant,
      current_state: data.current_state || 'initial',
      status: data.status || WorkflowStatus.Active,
      created_at: draft.updatedMs,
      updated_at: draft.updatedMs,
      context: data.context || {},
      timeline: data.timeline || [],
      assigned_actors: data.assigned_actors || [],
      created_tasks: data.created_tasks || [],
      allocated_resources: data.allocated_resources || [],
    };
  }

  private transformWorkflowToFormConfig(workflow: WorkflowTemplate): WorkflowFormConfig {
    return {
      workflow_name: workflow.template_name,
      display_name: workflow.display_name,
      description: workflow.description,
      form_fields: workflow.parameters.map((param) => ({
        name: param.name,
        field_type: param.parameter_type.toLowerCase(),
        display_name: param.display_name,
        description: param.description,
        required: param.required,
        default_value: param.default_value,
        component_type: param.ui_component.component_type,
        component_props: param.ui_component.props,
        validation_rules: param.validation_rules.map((rule) => rule.rule_type),
      })),
      ui_layout: workflow.ui_config?.form_layout
        ? {
            layout_type: workflow.ui_config.form_layout.layout_type,
            columns: workflow.ui_config.form_layout.columns,
            spacing: workflow.ui_config.form_layout.spacing,
            step_indicators: workflow.ui_config.step_indicators || [],
            field_groups: workflow.ui_config.field_groups || [],
            progressive_disclosure: false,
          }
        : this.getDefaultUILayout(),
      validation_rules: {},
      validation_messages: workflow.ui_config?.validation_messages || {},
      submission_config: {
        submit_button_text: workflow.ui_config?.submit_button_text || 'Submit',
        cancel_button_text: workflow.ui_config?.cancel_button_text || 'Cancel',
        confirmation_required: false,
        auto_save: false,
        save_draft: true,
      },
    };
  }

  private getDefaultUIConfig(): WorkflowUIConfig {
    return {
      form_layout: {
        layout_type: 'vertical',
        columns: 1,
        spacing: 'md',
      },
      step_indicators: [],
      field_groups: [],
      validation_messages: {},
      submit_button_text: 'Submit',
      cancel_button_text: 'Cancel',
    };
  }

  private getDefaultUILayout(): UILayout {
    return {
      layout_type: 'vertical',
      columns: 1,
      spacing: 'md',
      step_indicators: [],
      field_groups: [],
      progressive_disclosure: false,
    };
  }

  /**
   * Send signal to existing workflow instance
   * Life-critical: Must maintain audit trail and state consistency
   */
  async sendSignal(
    instanceId: string,
    signalName: string,
    signalData: Record<string, unknown>,
    userId: string,
    sessionId: string,
    tenantId: string,
  ): Promise<{ success: boolean; instance_id: string; new_state?: string }> {
    try {
      const result = await this.instanceRepository.sendSignal({
        instanceId,
        signalName,
        signalData,
        userId,
        sessionId,
        tenantId,
      });

      if (result?.success) {
        return {
          success: true,
          instance_id: result.instanceId,
          new_state: result.newState,
        };
      } else {
        throw new Error(result?.error || 'Failed to send workflow signal');
      }
    } catch (error) {
      console.error('Error sending workflow signal:', error);
      throw error;
    }
  }
}

// Workflow instance type matching backend
export interface WorkflowInstance {
  id: string;
  workflow_name: string;
  workflow_version: string;
  tenant_id: string;
  current_state: string;
  status: WorkflowStatus;
  created_at: number;
  updated_at: number;
  context: Record<string, unknown>;
  timeline: TimelineItem[];
  assigned_actors: Actor[];
  created_tasks: Task[];
  allocated_resources: Resource[];
}

export enum WorkflowStatus {
  Active = 'Active',
  Suspended = 'Suspended',
  Completed = 'Completed',
  Failed = 'Failed',
  Cancelled = 'Cancelled',
  Running = 'Running',
  Paused = 'Paused',
}

// FERP primitive types used in workflows
export interface TimelineItem {
  at_ms: number;
  transition: string;
  actor: string;
  payload: Record<string, unknown>;
}

export interface Actor {
  id: string;
  name: string;
  kind: string;
  roles: string[];
  metadata: Record<string, string>;
}

export interface Task {
  id: string;
  title: string;
  kind: string;
  status: string;
  assignees: string[];
  metadata: Record<string, string>;
}

export interface Resource {
  id: string;
  name: string;
  resource_type: string;
  allocation: {
    tenant: string;
    environment: string;
    cost_center: string;
  };
  constraints: Record<string, string>;
}

// Utility functions for workflow management
export const workflowUtils = {
  /**
   * Get human-readable category name
   */
  getCategoryDisplayName(category: WorkflowCategory): string {
    const categoryNames: Record<WorkflowCategory, string> = {
      [WorkflowCategory.UserManagement]: 'User Management',
      [WorkflowCategory.DocumentApproval]: 'Document Approval',
      [WorkflowCategory.ComplianceReview]: 'Compliance Review',
      [WorkflowCategory.SystemIntegration]: 'System Integration',
      [WorkflowCategory.DataProcessing]: 'Data Processing',
      [WorkflowCategory.SecurityIncident]: 'Security Incident',
      [WorkflowCategory.TenantOnboarding]: 'Tenant Onboarding',
      [WorkflowCategory.PolicyManagement]: 'Policy Management',
      [WorkflowCategory.AuditProcess]: 'Audit Process',
      [WorkflowCategory.BusinessProcess]: 'Business Process',
    };
    return categoryNames[category] || category;
  },

  /**
   * Get status badge color
   */
  getStatusBadgeColor(status: WorkflowStatus): string {
    const statusColors: Record<WorkflowStatus, string> = {
      [WorkflowStatus.Active]: 'blue',
      [WorkflowStatus.Suspended]: 'yellow',
      [WorkflowStatus.Completed]: 'green',
      [WorkflowStatus.Failed]: 'red',
      [WorkflowStatus.Cancelled]: 'gray',
      [WorkflowStatus.Running]: 'cyan',
      [WorkflowStatus.Paused]: 'orange',
    };
    return statusColors[status] || 'gray';
  },

  /**
   * Format timeline item for display
   */
  formatTimelineItem(item: TimelineItem): string {
    const date = new Date(item.at_ms);
    const timeStr = date.toLocaleString();
    return `[${timeStr}] ${item.transition} by ${item.actor}`;
  },

  /**
   * Get required compliance frameworks from template
   */
  getRequiredComplianceFrameworks(template: WorkflowTemplate): string[] {
    return template.compliance_frameworks.filter((framework) =>
      ['HIPAA', 'SOX', 'GDPR', 'FDA_21_CFR_Part_11'].includes(framework),
    );
  },

  /**
   * Check if user has required roles for workflow
   */
  hasRequiredRoles(userRoles: string[], template: WorkflowTemplate): boolean {
    if (template.required_roles.length === 0) {
      return true;
    }
    return template.required_roles.some((role) => userRoles.includes(role));
  },

  /**
   * Generate workflow form validation schema
   */
  generateValidationSchema(config: WorkflowFormConfig) {
    return createWorkflowFormSchema(config);
  },

  /**
   * Extract field dependencies for conditional display
   */
  extractFieldDependencies(config: WorkflowFormConfig): Record<string, string[]> {
    const dependencies: Record<string, string[]> = {};

    config.form_fields.forEach((field) => {
      if (field.depends_on) {
        if (!dependencies[field.depends_on]) {
          dependencies[field.depends_on] = [];
        }
        dependencies[field.depends_on]!.push(field.name);
      }
    });

    return dependencies;
  },

  /**
   * Group fields by field groups for layout
   */
  groupFieldsByGroups(config: WorkflowFormConfig): Record<string, FormField[]> {
    const grouped: Record<string, FormField[]> = {};

    // Create groups from ui_layout field_groups
    config.ui_layout.field_groups.forEach((group) => {
      grouped[group.title] = config.form_fields.filter((field) =>
        group.fields.includes(field.name),
      );
    });

    // Add ungrouped fields to a default group
    const groupedFieldNames = new Set(
      Object.values(grouped)
        .flat()
        .map((field) => field.name),
    );
    const ungroupedFields = config.form_fields.filter(
      (field) => !groupedFieldNames.has(field.name),
    );

    if (ungroupedFields.length > 0) {
      grouped['Other'] = ungroupedFields;
    }

    return grouped;
  },
};

// Export commonly used workflow template names
export const SYSTEM_WORKFLOW_TEMPLATES = {
  USER_DETAILS_MANAGEMENT: 'user_details_management',
  DOCUMENT_APPROVAL: 'document_approval',
  COMPLIANCE_REVIEW: 'compliance_review',
  TENANT_ONBOARDING: 'tenant_onboarding',
  SECURITY_INCIDENT: 'security_incident',
  TENANT_IAM_SELF_SERVICE: 'tenant_iam_self_service',
} as const;

export type SystemWorkflowTemplate =
  (typeof SYSTEM_WORKFLOW_TEMPLATES)[keyof typeof SYSTEM_WORKFLOW_TEMPLATES];
